import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { DataTable } from "../shared/DataTable";
import { Pagination } from "../shared/Pagination";
import { StatusBadge } from "../shared/StatusBadge";
import { ConfirmDialog } from "../shared/ConfirmDialog";
import { Input } from "../ui/input";
import { ItemFormDialog } from "./ItemFormDialog";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";
import { itemStatusMeta } from "../../lib/statusMaps";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useListState } from "../../hooks/useListState";

const LIMIT = 20;

export function ItemsTab() {
  const [state, setState] = useState({ loading: true, rows: [], total: 0 });
  const [page, setPage] = useListState("catalog.items.page", 0);
  const [search, setSearch] = useListState("catalog.items.search", "");
  const debouncedSearch = useDebouncedValue(search, 400);
  const [dialogItem, setDialogItem] = useState(undefined);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const refresh = () => {
    setState((s) => ({ ...s, loading: true }));
    apiClient.get("/items", { params: { q: debouncedSearch || undefined, limit: LIMIT, offset: page * LIMIT } })
      .then(({ data }) => setState({ loading: false, rows: data.items, total: data.total }))
      .catch((error) => { toast.error(getApiErrorMessage(error, "Could not load items.")); setState((s) => ({ ...s, loading: false })); });
  };

  useEffect(refresh, [page, debouncedSearch]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await apiClient.delete(`/items/${deleteTarget.id}`);
      toast.success("Item deleted");
      setDeleteTarget(null);
      refresh();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not delete item."));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="mt-4" data-testid="items-tab">
      <div className="mb-4 flex items-center justify-between gap-3">
        <Input
          placeholder="Search items..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          className="max-w-xs"
          data-testid="items-search-input"
        />
        <Button size="sm" onClick={() => setDialogItem(null)} data-testid="items-new-button">
          <Plus size={15} strokeWidth={1.5} /> New Item
        </Button>
      </div>

      <DataTable
        testId="items-table"
        loading={state.loading}
        rows={state.rows}
        emptyTitle="No items yet"
        columns={[
          { key: "name", header: "Name" },
          { key: "quantity", header: "Quantity", render: (r) => r.quantity || "1" },
          { key: "status", header: "Status", render: (r) => { const m = itemStatusMeta(r.status); return <StatusBadge label={m.label} variant={m.variant} />; } },
          {
            key: "actions", header: "", render: (r) => (
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="icon" onClick={() => setDialogItem(r)} data-testid={`items-edit-${r.id}`}>
                  <Pencil size={14} strokeWidth={1.5} />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(r)} data-testid={`items-delete-${r.id}`}>
                  <Trash2 size={14} strokeWidth={1.5} className="text-danger" />
                </Button>
              </div>
            ),
          },
        ]}
      />
      <Pagination page={page} onPageChange={setPage} total={state.total} pageSize={LIMIT} testId="items-pagination" />

      <ItemFormDialog
        open={dialogItem !== undefined} onOpenChange={(v) => !v && setDialogItem(undefined)}
        item={dialogItem} onSaved={() => { setDialogItem(undefined); refresh(); }}
      />
      <ConfirmDialog
        open={Boolean(deleteTarget)} onOpenChange={(v) => !v && setDeleteTarget(null)}
        title="Delete this item?" description={`"${deleteTarget?.name}" will be marked deleted. Plans already using it are unaffected.`}
        confirmLabel="Delete Item" variant="danger" loading={deleting} onConfirm={handleDelete}
        testId="items-delete-confirm-dialog"
      />
    </div>
  );
}
